'use client';
import React, { useState } from 'react';
import { Globe2, Sparkles, ArrowRight } from 'lucide-react';
import type { WizardData } from './VisaWizard';

interface Props {
  data: WizardData;
  updateData: (d: Partial<WizardData>) => void;
  onNext: () => void;
  onBack: () => void;
}

const languages = [
  { code: 'en', label: 'English', native: 'English', flag: '🇺🇸' },
  { code: 'ar', label: 'Arabic', native: 'العربية', flag: '🇸🇦' },
  { code: 'ur', label: 'Urdu', native: 'اردو', flag: '🇵🇰' },
  { code: 'hi', label: 'Hindi', native: 'हिंदी', flag: '🇮🇳' },
];

const purposes = [
  { id: 'tourist', emoji: '🏖️', label: 'Tourism & travel', sub: 'Leisure, sightseeing, holiday' },
  { id: 'work', emoji: '💼', label: 'Work or employment', sub: 'Job offer, transfer, assignment' },
  { id: 'student', emoji: '🎓', label: 'Study & education', sub: 'University, college, language school' },
  { id: 'business', emoji: '🤝', label: 'Business trip', sub: 'Meetings, conferences, trade' },
  { id: 'family', emoji: '👨‍👩‍👧', label: 'Family visit or reunion', sub: 'Visiting relatives, family sponsorship' },
  { id: 'transit', emoji: '✈️', label: 'Airport transit', sub: 'Layover, connecting flight' },
];

export default function Step1Welcome({ data, updateData, onNext }: Props) {
  const [selectedLang, setSelectedLang] = useState(data.language || 'en');
  const [selectedPurpose, setSelectedPurpose] = useState(data.travelPurpose || '');

  const canContinue = selectedPurpose !== '';

  const handleContinue = () => {
    updateData({ language: selectedLang, travelPurpose: selectedPurpose });
    onNext();
  };

  return (
    <div className="wizard-card space-y-8 animate-slide-up">
      {/* Header */}
      <div className="text-center space-y-2">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 border border-blue-100 rounded-full mb-2">
          <Sparkles size={13} className="text-blue-500" />
          <span className="text-xs font-semibold text-blue-700">AI Visa Assistant</span>
        </div>
        <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight">
          Welcome to VisaPilot AI
        </h1>
        <p className="text-gray-500 text-sm leading-relaxed max-w-md mx-auto">
          Answer a few simple questions and we&apos;ll build your complete visa application package — checklist, cover letter, and all supporting documents.
        </p>
      </div>

      {/* Language selector */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Globe2 size={15} className="text-blue-500" />
          <label className="text-sm font-bold text-gray-700">Choose your language</label>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {languages.map((lang) => (
            <button
              key={`lang-select-${lang.code}`}
              onClick={() => setSelectedLang(lang.code)}
              className={`flex flex-col items-center gap-1 p-3 rounded-xl border text-center transition-all duration-150 ${
                selectedLang === lang.code
                  ? 'border-blue-300 bg-blue-50 ring-2 ring-blue-100' :'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
              }`}
            >
              <span className="text-xl">{lang.flag}</span>
              <span className={`text-xs font-bold ${selectedLang === lang.code ? 'text-blue-700' : 'text-gray-700'}`}>
                {lang.label}
              </span>
              <span className="text-xs text-gray-400">{lang.native}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Purpose selector */}
      <div className="space-y-3">
        <label className="text-sm font-bold text-gray-700">What are you applying for?</label>
        <p className="text-xs text-gray-400">Select the main reason for your trip</p>
        <div className="grid sm:grid-cols-2 gap-2.5">
          {purposes.map((p) => (
            <button
              key={`purpose-${p.id}`}
              onClick={() => setSelectedPurpose(p.id)}
              className={`flex items-center gap-3 p-3.5 rounded-xl border text-left transition-all duration-150 group ${
                selectedPurpose === p.id
                  ? 'border-blue-300 bg-blue-50 ring-2 ring-blue-100' :'border-gray-200 bg-white hover:border-blue-200 hover:bg-blue-50/30'
              }`}
            >
              <span className="text-2xl shrink-0">{p.emoji}</span>
              <div>
                <p className={`text-sm font-bold ${selectedPurpose === p.id ? 'text-blue-800' : 'text-gray-800'}`}>
                  {p.label}
                </p>
                <p className="text-xs text-gray-400">{p.sub}</p>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className="pt-2">
        <button
          onClick={handleContinue}
          disabled={!canContinue}
          className="btn-primary w-full justify-center py-3 text-sm"
        >
          Continue to visa selection
          <ArrowRight size={15} />
        </button>
        {!canContinue && (
          <p className="text-xs text-center text-gray-400 mt-2">Select a travel purpose to continue</p>
        )}
      </div>
    </div>
  );
}